import { AustralianStateCode } from "./matchLocationToState.util";
import resolveStateFromCoordinates from "./resolveStateFromCoordinates.util";

/*-- Every failure here used to collapse into the same silent undefined, which the caller treats as
     "unknown location" and answers with the first contact card. Logging the reason keeps a blocked
     permission distinguishable from a visitor who is genuinely outside Australia. --*/
const warn = (reason: string): void => {
  if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") {
    console.warn(`[getCurrentUserState] ${reason} — falling back to the first contact card.`);
  }
};

/*-- Ask the browser for the visitor's position, then resolve it to a state code. Resolves undefined
     whenever geolocation is unavailable, declined, times out or lands outside Australia. --*/
const getCurrentUserState = (): Promise<AustralianStateCode | undefined> =>
  new Promise((resolve: (state: AustralianStateCode | undefined) => void) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      warn("geolocation is unavailable in this browser");
      resolve(undefined);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        const {
          coords: { latitude, longitude },
        } = position;

        const state = resolveStateFromCoordinates(latitude, longitude);

        if (!state) {
          warn(`${latitude},${longitude} is outside Australia`);
        }

        resolve(state);
      },
      (error: GeolocationPositionError) => {
        // A cross-origin preview iframe without allow="geolocation" fails here with PERMISSION_DENIED,
        // the same code as a visitor declining the browser prompt.
        warn(`geolocation failed (code ${error.code}: ${error.message || "no detail"})`);
        resolve(undefined);
      },
      { timeout: 10000, maximumAge: 300000 },
    );
  });

export default getCurrentUserState;
